// ...existing code...
import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSearch, FaTimes } from "react-icons/fa";
import { useSelector } from "react-redux";
import typeOptionValue from "../../i18n/type_option_value.json"; // Adjust the path accordingly
import InfoTabs from "../user/InfoTabs";
import AdCard from "./AdCard";
import AdModal from "./AdModal";
import GradeLevelSelector from "./GradeLevelSelector";
import LocationSelector from "./LocationSelector";
import SubjectSelector from "./SubjectSelector";

const SearchTutor = () => {
  const { t } = useTranslation();

  const handleSubjectChange = (value) => {
    setSelectedSubjectTypes((prev) =>
      prev.includes(value)
        ? prev.filter((subject) => subject !== value)
        : [...prev, value]
    );
  };

  const handleFormGroupChange = (value) => {
    setSelectedFormGroup((prev) =>
      prev.includes(value)
        ? prev.filter((group) => group !== value)
        : [...prev, value]
    );
  };

  const handleClear = () => {
    setSelectedSubjects([]);
    setSelectedGrades([]);
    setSelectedLocations([]);
    setSelectedSubjectTypes([]);
    setSelectedFormGroup([]);
    setPage(1);
  };
  const auth = useSelector((state) => state.auth); // Retrieve user data
  const user = auth.user;
  const token = auth.token;

  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [SelectedLocations, setSelectedLocations] = useState([]);
  const [selectedSubjectTypes, setSelectedSubjectTypes] = useState([]);
  const [selectedFormGroup, setSelectedFormGroup] = useState([]);

  const [ads, setAds] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);

  // Reset page to 1 when any search filter changes
  useEffect(() => {
    setPage(1);
  }, [
    selectedSubjects,
    selectedGrades,
    SelectedLocations,
    selectedSubjectTypes,
    selectedFormGroup,
  ]);

  // Dialog state for ad info
  const [selectedAd, setSelectedAd] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (showDialog) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showDialog]);

  // Dialog state for profile info
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  // Handle screen size changes
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const handleResize = () => {
    const screenWidth = window.innerWidth;
    setIsLargeScreen(screenWidth >= 1024);
    setShowFilters(screenWidth >= 1024);
  };
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isSticky, setSticky] = useState(false);
  const handleScroll = () => {
    // Get the current scroll position
    const currentScrollTop =
      document.documentElement.scrollTop || window.scrollY;

    // Check if the current scroll position is greater than the sticky offset
    const stickyOffset = document.getElementById("stickyDiv").offsetTop;
    setSticky(currentScrollTop > stickyOffset);
  };

  // Sticky header scroll
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Infinite scroll: load next page when near bottom
  useEffect(() => {
    let debounceTimeout = null;
    const handleInfiniteScroll = () => {
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        const scrollY = window.scrollY || window.pageYOffset;
        const viewportHeight = window.innerHeight;
        const fullHeight = document.documentElement.scrollHeight;
        // If near bottom, load next page if available
        if (
          pagination &&
          page < pagination.total_pages &&
          fullHeight - (scrollY + viewportHeight) < 200
        ) {
          setPage((p) => (p < pagination.total_pages ? p + 1 : p));
        }
      }, 100);
    };
    window.addEventListener("scroll", handleInfiniteScroll);
    return () => {
      window.removeEventListener("scroll", handleInfiniteScroll);
      if (debounceTimeout) clearTimeout(debounceTimeout);
    };
  }, [pagination, page]);

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Reset ads and page to 1 when filters change
  useEffect(() => {
    setAds([]);
    setPage(1);
  }, [
    selectedSubjects,
    selectedGrades,
    SelectedLocations,
    selectedSubjectTypes,
    selectedFormGroup,
  ]);

  useEffect(() => {
    const fetchAds = async () => {
      if (page >= 1) setLoadingNextPage(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/ads.php`,
          {
            params: {
              advance: 1,
              adType: "tutor",
              subject: selectedSubjects.join(","),
              subjectType: selectedSubjectTypes.join(","),
              location: SelectedLocations.join(","),
              formGroupType: selectedFormGroup.join(","),
              gradeLevel: selectedGrades.join(","),
              page: page,
            },
          }
        );
        if (response.data.success) {
          setAds((prevAds) =>
            page === 1
              ? response.data.ad.ads
              : [...prevAds, ...response.data.ad.ads]
          );
          setPagination(response.data.ad.pagination);
        } else {
          setAds([]);
          setPagination(null);
        }
      } catch (error) {
        console.error("Error fetching ads:", error);
      } finally {
        setLoadingNextPage(false);
      }
    };
    fetchAds();
  }, [
    selectedSubjects,
    selectedGrades,
    SelectedLocations,
    selectedSubjectTypes,
    selectedFormGroup,
    page,
  ]);
  // ...existing code...

  // Handler for FaDiscourse icon click (show ad info)
  const handleAdInfoClick = (ad) => {
    setSelectedAd(ad);
    setShowDialog(true);
  };

  // Handler for Avatar click (show profile)
  const handleAvatarClick = (userId, ad) => {
    setSelectedProfile(ad); // Use ad as profile info (customize as needed)
    setShowProfileDialog(true);
  };
  const handleCloseProfileDialog = () => {
    setShowProfileDialog(false);
    setSelectedProfile(null);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedAd(null);
  };

  return (
    <div className="grid lg:grid-cols-[300px,1fr] max-w-screen">
      <div
        id="stickyDiv" //搜索 button panel
        className={`bg-white shadow-md rounded-md  
            duration-300 lg:static lg:h-auto lg:grid-cols-1   
            ${isSticky ? "fixed top-0 left-0 w-full z-10" : "relative"}
          `}
      >
        <div className="p-4">
          <div className="flex justify-between items-center">
            <button
              onClick={handleClear}
              className="bg-gray-500 hover:bg-gray-300 text-white font-bold py-2 px-4 rounded"
            >
              {t("clear")}
            </button>
            <h2 className="text-2xl font-bold text-orange-500">
              {t("startSearching")}
            </h2>
            {!isLargeScreen && (
              <button
                onClick={toggleFilters}
                className="bg-orange-400 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded flex"
              >
                <FaSearch size={20} className="mr-2" />
                {showFilters ? t("searchResult") : t("Search")}
              </button>
            )}
          </div>
          {/* 搜索 Results */}
          {showFilters && (
            <div className="mt-4">
              <label>{t("ad.formGroupType")}</label>
              <div className="flex flex-wrap">
                {Object.entries(typeOptionValue.form_group_types).map(
                  ([key, { display }]) => (
                    <span
                      key={key}
                      onClick={() => handleFormGroupChange(key)}
                      className={`Select-tag  ${
                        selectedFormGroup.includes(key)
                          ? "bg-blue-500 text-white"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      {display[t("language")]}
                    </span>
                  )
                )}
              </div>
              <GradeLevelSelector
                selectedGrades={selectedGrades}
                setSelectedGrades={setSelectedGrades}
              />
              <label>{t("ad.subjectType")}</label>
              <div className="flex flex-wrap">
                {Object.entries(typeOptionValue.subject_types).map(
                  ([key, { display }]) => (
                    <span
                      key={key}
                      onClick={() => handleSubjectChange(key)}
                      className={`Select-tag    ${
                        selectedSubjectTypes.includes(key)
                          ? "bg-blue-500 text-white"
                          : "bg-gray-200 text-gray-800"
                      }`}
                    >
                      {display[t("language")]}
                    </span>
                  )
                )}
              </div>
              <SubjectSelector
                selectedSubjects={selectedSubjects}
                setSelectedSubjects={setSelectedSubjects}
              />
              <LocationSelector
                SelectedLocations={SelectedLocations}
                setSelectedLocations={setSelectedLocations}
              />
            </div>
          )}
        </div>
      </div>
      <div
        className={`bg-white m-1 p-2 shadow-md rounded-md  lg:mt-0 
      ${isLargeScreen ? "" : showFilters ? "hidden" : ""}`}
      >
        <div className=" grid grid-cols-1 md:grid-cols-2  gap-1 ">
          {ads && ads.length > 0 ? (
            ads.map((ad) => (
              <AdCard
                ad={ad}
                key={ad.id}
                onAvatarClick={handleAvatarClick}
                onAdInfoClick={handleAdInfoClick}
              />
            ))
          ) : (
            <div className="flex flex-col m-10 text-gray-500 items-center ">
              <FaSearch size={29} />
              {!loadingNextPage && <div>{t("noResults")}</div>}
            </div>
          )}
        </div>
        {/* Infinite scroll: no Pagination Controls needed */}
        {loadingNextPage && (
          <div className="flex justify-center items-center my-4">
            <svg
              className="animate-spin h-8 w-8 text-orange-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
            <span className="ml-2 text-orange-500 font-semibold">
              {t("loading")}
            </span>
          </div>
        )}

        {/* Dialog/modal for ad info */}
        <AdModal
          show={showDialog}
          ad={selectedAd}
          onClose={handleCloseDialog}
          user={user}
          token={token}
          t={t}
          onAvatarClick={handleAvatarClick}
          type="tutor"
        />

        {/* Dialog/modal for profile info */}
        {showProfileDialog && selectedProfile && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={handleCloseProfileDialog}
          >
            <div
              className="bg-white rounded-lg shadow-lg pt-5 p-1 max-w-lg w-full relative"
              onClick={(e) => e.stopPropagation()}
              style={{ height: "99%", maxHeight: "99%" }}
            >
              <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
                onClick={handleCloseProfileDialog}
              >
                <FaTimes size={24} />
              </button>
              <h2 className="text-xl font-bold mb-2">
                {selectedProfile.username || t("profile")}
              </h2>
              <div style={{ overflowY: "auto", height: "calc(99% - 40px)" }}>
                <InfoTabs userId={selectedProfile.userId} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchTutor;
